/**
 * Chat poll contracts — server-verified poll close.
 *
 * Stream Chat has NO server-side poll deadline: the only close mechanism is a
 * manual `closePoll` call flipping `is_closed`. The mobile app stamps a custom
 * `close_date` on each poll at creation and, until now, enforced it purely
 * client-side (per-device timer) — so the poll stayed genuinely open on
 * Stream past its deadline, and devices could disagree.
 *
 * `POST /v1/groups/chat/polls/close` is the lazy, server-verified close
 * trigger: any channel member's device may call it when it observes the
 * deadline pass. The backend re-reads the poll from Stream, checks the
 * deadline against ITS OWN clock (never the device's), and closes via the
 * server client. Rules:
 *  - the poll creator may close anytime (parity with the in-card Close button)
 *  - anyone else may trigger the close ONLY once `close_date` has passed
 *  - idempotent: an already-closed poll returns success (`alreadyClosed`)
 *  - authorization: the caller must be a member of the poll message's channel.
 *    `messageId` is required so the backend can resolve that channel (a poll
 *    carries no channel of its own) and verify membership — pollId alone is
 *    NOT a tenant boundary on the shared Stream app.
 */

import { z } from 'zod'

export const ChatPollCloseRequestSchema = z.object({
  /** Stream poll id (from the poll message's `poll_id`). */
  pollId: z.string().min(1),
  /**
   * Id of the Stream message carrying this poll. Required: the backend resolves
   * the message's channel from it and checks the caller is a member of that
   * channel (the real access boundary), and binds it back to `pollId` so a
   * caller can't pair an arbitrary message with a foreign poll.
   */
  messageId: z.string().min(1)
})

export const ChatPollCloseResponseSchema = z.object({
  success: z.literal(true),
  /** True when the poll was already closed on Stream (idempotent no-op). */
  alreadyClosed: z.boolean()
})

export type ChatPollCloseRequest = z.infer<typeof ChatPollCloseRequestSchema>
export type ChatPollCloseResponse = z.infer<typeof ChatPollCloseResponseSchema>
